import { create } from "zustand"
import { type IEnergyPassportDetails, IEnergyPassportResourceType } from "@/entities/energy-passport/types.ts"
import { generateInitialEnergyPassportDetails } from "@/features/energy-passport/ui/main-table/model/static-data.ts"

export type IEnergyPassportDetailsEditableField = keyof Omit<IEnergyPassportDetails, "id" | "tempId" | "updated_at" | "quarter" | "">

export interface IEnergyPassportDetailsStoreValue {
  quarters: IEnergyPassportDetails[]
  expanded?: boolean
  type: IEnergyPassportResourceType
}

export type IEnergyPassportDetailsStore = {
  [key in IEnergyPassportResourceType]: {
    quarters: IEnergyPassportDetails[]
    expanded?: boolean
    type: key
  }
}

interface IEnergyPassportDetailsActions {
  setDetails: (quarters: Partial<IEnergyPassportDetailsStore>) => void
  toggleQuarterRow: (type: IEnergyPassportResourceType) => void
  updateQuarter: (type: IEnergyPassportResourceType, editedQuarter: Partial<IEnergyPassportDetails>) => void
}

function getInitialState<T extends keyof IEnergyPassportDetailsStore>(type: T): Pick<IEnergyPassportDetailsStore, T> {
  return {
    [type]: {
      type: type,
      expanded: false,
      quarters: generateInitialEnergyPassportDetails(type)
    }
  } as Pick<IEnergyPassportDetailsStore, T>
}

export const initialEnergyPassportDetailsStore: IEnergyPassportDetailsStore = {
  ...getInitialState(IEnergyPassportResourceType.ACTIVE_ELECTRICITY),
  ...getInitialState(IEnergyPassportResourceType.REACTIVE_ELECTRICITY),
  ...getInitialState(IEnergyPassportResourceType.HEAT),
  ...getInitialState(IEnergyPassportResourceType.GAS),
  ...getInitialState(IEnergyPassportResourceType.MAZUT),
  ...getInitialState(IEnergyPassportResourceType.MAZUT),
  ...getInitialState(IEnergyPassportResourceType.COAL),
  ...getInitialState(IEnergyPassportResourceType.PETROL),
  ...getInitialState(IEnergyPassportResourceType.DIESEL),
  ...getInitialState(IEnergyPassportResourceType.KEROSENE),
  ...getInitialState(IEnergyPassportResourceType.LUBRICANT),
  ...getInitialState(IEnergyPassportResourceType.OTHER_OIL_PRODUCT),
  ...getInitialState(IEnergyPassportResourceType.WATER)
}
export const useEnergyPassportDetailsStore = create<IEnergyPassportDetailsStore & IEnergyPassportDetailsActions>()((set, get) => ({
  ...initialEnergyPassportDetailsStore,
  setDetails: details =>
    set(() => ({
      ...details,
      setDetails: get().setDetails,
      toggleQuarterRow: get().toggleQuarterRow,
      updateQuarter: get().updateQuarter
    })),
  toggleQuarterRow: type =>
    set({
      [type]: {
        ...get()[type],
        expanded: !get()[type].expanded
      }
    }),
  updateQuarter: (type, editedQuarter) =>
    set({
      [type]: {
        ...get()[type],
        quarters: get()[type].quarters.map(q => {
          return q.quarter === editedQuarter.quarter ? { ...q, ...editedQuarter } : q
        })
      }
    })
}))
